import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFilesForEnvironment } from '../../api/documentAPI';
import { deleteFiles } from '../../api/documentAPI';
import { useAtom } from 'jotai';
import { allowDeleteAtom } from '../../atoms/managedocs/useAllowDeleteAtom';
import { useSelectedEnvTypeAtom } from '../../atoms/managedocs/useSelectedEnvTypeAtom';
import { docsRefreshAtom } from '../../atoms/managedocs/useDocsRefreshAtom';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType } from '../../enums/NotificationType';

import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import config from '../../config/configJS';
import { appLocale } from '../../utils/dateFormat';

// Zoznam sa načítava po stránkach (aj pri 100 000 dokumentoch v priečinku sa prenesie len jedna stránka).
const PAGE_SIZE = 100;
// Poradie filtrov podľa stavu (zobrazia sa len stavy, ktoré v priečinku sú)
const STATUS_ORDER = ['IN_PROGRESS', 'NEW', 'UPDATE', 'DONE', 'ERROR', 'TO_BE_DELETED'];

// Kľúč zoznamu na porovnanie – prekresľujeme len keď sa niektorý dokument (Id/Status/názov/url) zmení.
const _docsKey = (list) => (Array.isArray(list) ? list : [])
  .map(d => `${d.Id}|${d.Status}|${d.FileName}|${d.Url ?? ''}`).join('\n');

const DocumentsList = ({ id }) => {

    const { showNotification } = useNotification();

    const [t, i18n] = useTranslation('global');
    // čísla podľa jazyka aplikácie (1 288 / 1,288)
    const fmt = (n) => Number(n || 0).toLocaleString(appLocale(i18n.language));
    const listTopRef = useRef(null);

    const [allowDelete, ] = useAtom(allowDeleteAtom);
    const [selectedEnvType,] = useSelectedEnvTypeAtom();
    const [docsRefresh] = useAtom(docsRefreshAtom);

    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [open, setOpen] = React.useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());  // výber na mazanie (doc.Id)

    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [statusCounts, setStatusCounts] = useState({});
    const [status, setStatus] = useState(null);       // filter stavu (null = všetky)
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');         // hľadanie s oneskorením (nepýta sa servera pri každom písmene)

    // Aktuálne parametre pre 15 s poll (interval nesmie čítať zastarané hodnoty zo stavu)
    const queryRef = useRef({ id, page, search, status });
    queryRef.current = { id, page, search, status };

    const toggleSelected = (docId) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(docId)) next.delete(docId); else next.add(docId);
        return next;
      });
    };

    const allSelected = documents.length > 0 && documents.every(d => selectedIds.has(d.Id));
    const toggleSelectAll = () => {
      setSelectedIds(allSelected ? new Set() : new Set(documents.map(d => d.Id)));
    };

    const handleBulkOpen = () => {
      if (selectedIds.size === 0) return;
      setOpen(true);
    }

    const handleClose = () => {
      setOpen(false);
    }

    const handleNo = () => {
      handleClose();
    }

    const handleYes = () => {
      handleBulkDelete();
      handleClose();
    }

    const handleBulkDelete = async () => {
      const ids = [...selectedIds];
      if (ids.length === 0) return;
      setIsLoading(true);
      try {
        await deleteFiles(ids);
        setSelectedIds(new Set());
        await fetchData();
        showNotification({ message: t('filesDeleted'), type: NotificationType.Success });
      } finally {
        setIsLoading(false);
      }
    }

    const fetchData = async () => {
      const q = queryRef.current;
      if (!q.id) {
        setDocuments([]);
        setIsLoading(false);
        return;
      }

      try {
        const data = await getFilesForEnvironment(q.id, { page: q.page, pageSize: PAGE_SIZE, search: q.search, status: q.status });
        const current = queryRef.current;
        if (current.id !== q.id || current.page !== q.page || current.search !== q.search || current.status !== q.status) {
          return;  // medzitým sa zmenil priečinok/stránka/filter -> neprepisuj novší zoznam starou odpoveďou
        }
        const next = Array.isArray(data?.Files) ? data.Files : [];
        // Prekresli LEN keď sa zoznam reálne zmenil: pri rovnakom obsahu vráť pôvodnú
        // referenciu -> React re-render preskočí (žiadne zbytočné bliknutie pri každom polle).
        setDocuments(prev => (_docsKey(prev) === _docsKey(next) ? prev : next));
        setTotal(data?.Total ?? next.length);
        setPages(data?.Pages ?? 1);
        setStatusCounts(data?.StatusCounts ?? {});
        if (data?.Page && data.Page !== q.page) setPage(data.Page);   // stránka zanikla (napr. po zmazaní)
        // Poll-safety: nechaj vo výbere len dokumenty, ktoré sú ešte na stránke
        setSelectedIds(prev => {
          if (prev.size === 0) return prev;
          const present = new Set(next.map(d => d.Id));
          const pruned = new Set([...prev].filter(x => present.has(x)));
          return pruned.size === prev.size ? prev : pruned;
        });
      } catch (error) {
        console.warn('Failed to fetch documents for environment', q.id, error);
        setDocuments([]);
      } finally {
        setIsLoading(false);
      }
    }

    // Zmena priečinka -> od prvej stránky, bez filtra a hľadania
    useEffect(() => {
      setPage(1);
      setStatus(null);
      setSearchInput('');
      setSearch('');
      setDocuments([]);
      setTotal(0);
      setStatusCounts({});
      setIsLoading(!!id);   // prepnutie prostredia → ukáž loading, nie prázdny/starý zoznam
    }, [id]);

    // Hľadanie: server sa pýta až 400 ms po poslednom písmene
    useEffect(() => {
      const h = setTimeout(() => {
        if (searchInput.trim() !== search) {
          setSearch(searchInput.trim());
          setPage(1);
        }
      }, 400);
      return () => clearTimeout(h);
    }, [searchInput]);

    // Načítanie pri zmene priečinka/stránky/filtra + obnovovanie aktuálnej stránky každých 15 s
    useEffect(() => {
      if (!id) {
        setIsLoading(false);
        return;
      }
      fetchData();
      const intervalId = setInterval(() => {
          fetchData();
      }, 15000);
      // Cleanup function to clear the interval and prevent potential memory leaks
      return () => {
          clearInterval(intervalId);
      };
    }, [id, page, search, status]);

    // Zmena priečinka, stránky, filtra alebo prepnutie režimu mazania -> zruš výber
    useEffect(() => {
      setSelectedIds(new Set());
    }, [id, allowDelete, page, search, status]);

    // Okamžitý refresh po nahratí dokumentov (Uploader zvýši docsRefreshAtom) — nečaká na 15s poll
    useEffect(() => {
      if (id && docsRefresh > 0) {
        fetchData();
      }
    }, [docsRefresh]);

    const allCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);
    const statuses = STATUS_ORDER.filter(s => statusCounts[s] > 0);
    const filtered = !!(search || status);
    const chooseStatus = (s) => { setStatus(prev => (prev === s ? null : s)); setPage(1); };
    // zmena strany tlačidlami dole -> zoznam od začiatku (nie zostať na konci stránky)
    const goToPage = (p) => {
      setPage(Math.min(Math.max(1, p), pages));
      if (listTopRef.current) listTopRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

  return (
    <div className="document-list" ref={listTopRef}>
        <h3>{t('documents')}{allCount > 0 && <span className="doc-total"> ({fmt(allCount)})</span>}</h3>

        {(allCount > 0 || filtered) && (
          <div className="doc-toolbar">
            <input
              type="search"
              className="doc-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchDocuments') || 'Hľadať dokument…'}
            />
            <div className="doc-filters">
              <button type="button" className={'doc-filter' + (status === null ? ' active' : '')} onClick={() => chooseStatus(null)}>
                {t('allDocuments') || 'Všetky'} ({fmt(allCount)})
              </button>
              {statuses.map(s => (
                <button type="button" key={s} className={'doc-filter status-chip-' + s + (status === s ? ' active' : '')}
                        onClick={() => chooseStatus(s)}>
                  {t('status_' + s)} ({fmt(statusCounts[s])})
                </button>
              ))}
            </div>
            {filtered && !isLoading && (
              <span className="doc-found">{t('foundDocuments', { count: fmt(total) }) || `Nájdené: ${fmt(total)}`}</span>
            )}
          </div>
        )}

        {isLoading && documents.length === 0 && (
          <div className="doc-loading">
            <span className="material-symbols-outlined doc-loading-spin">progress_activity</span>
            <span>{t('loading') || 'Načítavam'}…</span>
          </div>
        )}
        {!isLoading && documents.length === 0 && <span>{filtered ? (t('noMatchingDocuments') || 'Žiadny dokument nezodpovedá hľadaniu.') : t('folderEmpty')}</span>}

        {allowDelete && documents.length > 0 && (
          <div className="doc-bulk-bar">
            <label className="doc-select-all">
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
              <span>{pages > 1 ? (t('selectAllOnPage') || 'Vybrať všetko na stránke') : t('selectAll')}</span>
            </label>
            <button
              className="bulk-delete-button"
              disabled={selectedIds.size === 0}
              onClick={handleBulkOpen}
            >
              {t('deleteSelected')} ({selectedIds.size})
            </button>
          </div>
        )}

        <ConfirmDeleteModal
            open={open}
            onNo={handleNo}
            onYes={handleYes}
            onClose={handleClose}
            item={selectedIds.size === 1
                ? (() => { const d = documents.find(x => selectedIds.has(x.Id)); return d ? (d.DisplayName || d.FileName) : ''; })()
                : `${t('selectedDocuments')} (${selectedIds.size})`}
        />

        {(documents ?? []).map((doc) => (
            <div key={doc.Id} className='doc-detail'>
                {allowDelete && (
                  <input
                    type="checkbox"
                    className="doc-select-checkbox"
                    checked={selectedIds.has(doc.Id)}
                    onChange={() => toggleSelected(doc.Id)}
                  />
                )}
                {/* tooltip = celá zdrojová cesta (disk/SharePoint) → odlíši rovnaké názvy z rôznych priečinkov */}
                <span className='doc-name' title={doc.SourcePath || doc.DisplayName || doc.FileName}>{doc.DisplayName || doc.FileName}</span>
                <div className='doc-status-wrap'>
                  <span className={'doc-status status-' + doc.Status}>{t('status_'+ doc.Status)}</span>

                  {(selectedEnvType === 'M' || selectedEnvType === 'N') ? (
                          <a
                            target="_blank"
                            href={`${config.endPoint}/api/millai/DownloadFile?intent=GetFileById&id=${doc.Id}`}
                            className="material-symbols-outlined download-item-icon"
                          >
                            download
                          </a>
                        ) : selectedEnvType === 'S' && (
                          <a
                            target="_blank"
                            href={doc.Url + '?web=1'}
                            className="material-symbols-outlined download-item-icon"
                          >
                            open_in_new
                          </a>
                    )}
                </div>
            </div>
        ))}

        {pages > 1 && (
          <div className="doc-pager">
            <button type="button" className="doc-page-btn" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
              <span className="material-symbols-outlined">chevron_left</span>{t('prevPage') || 'Predchádzajúca'}
            </button>
            <span className="doc-page-info">
              {t('pageOf', { page: fmt(page), pages: fmt(pages) }) || `Strana ${page} z ${pages}`}
              <span className="doc-page-total"> · {fmt(total)} {t('documentsShort') || 'dok.'}</span>
            </span>
            <button type="button" className="doc-page-btn" disabled={page >= pages} onClick={() => goToPage(page + 1)}>
              {t('nextPage') || 'Ďalšia'}<span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        )}
    </div>

  );
}

export default DocumentsList;
